import { useCallback } from 'react';
import { adminBroadcastService } from '../services/adminBroadcastService';

/**
 * React hook for broadcasting artist updates from admin panel
 * Automatically sends updates to mobile apps when content changes
 */
export const useAdminBroadcast = (artistId) => {

  /**
   * Broadcast profile changes
   */
  const broadcastProfile = useCallback(async (profileData) => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastProfileUpdate(artistId, profileData);
  }, [artistId]);

  /**
   * Broadcast works changes
   */
  const broadcastWorks = useCallback(async (workData, operation = 'UPDATE') => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastWorksUpdate(artistId, workData, operation);
  }, [artistId]);

  /**
   * Broadcast events changes
   */
  const broadcastEvents = useCallback(async (eventData, operation = 'UPDATE') => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastEventsUpdate(artistId, eventData, operation);
  }, [artistId]);

  /**
   * Broadcast about section changes
   */
  const broadcastAbout = useCallback(async (aboutData) => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastAboutUpdate(artistId, aboutData);
  }, [artistId]);

  /**
   * Broadcast promotions changes
   */
  const broadcastPromotions = useCallback(async (promotionsData) => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastPromotionsUpdate(artistId, promotionsData);
  }, [artistId]);

  /**
   * Broadcast bulk updates (multiple sections)
   */
  const broadcastBulk = useCallback(async (updates) => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.broadcastBulkUpdate(artistId, updates);
  }, [artistId]);

  /**
   * Test broadcast functionality
   */
  const testBroadcast = useCallback(async () => {
    if (!artistId) {
      console.warn('No artistId provided for broadcast');
      return { success: false, error: 'Missing artistId' };
    }

    return await adminBroadcastService?.testBroadcast(artistId);
  }, [artistId]);

  return {
    broadcastProfile,
    broadcastWorks,
    broadcastEvents,
    broadcastAbout,
    broadcastPromotions,
    broadcastBulk,
    testBroadcast
  };
};

export default useAdminBroadcast;